//@flow
import React, {useEffect, useState} from 'react';
import {Button, makeStyles, CircularProgress} from '@material-ui/core';
import {Link} from 'react-router-dom';
import SortableList, {SortableItem, SortableKnob} from 'react-easy-sort';

import {Add, DragIndicator} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import BannerItem from './BannerItem';
import BannerPreview from './BannerPreview';
import {useBannerActions, useBannerGroupActions} from '../../../utils';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';

import type {BannerGroup, Banner} from '../../../../../entities';
import SwitchableSection from '../../../../../elements/SwitchableSection';
import {deleteBannerGroupService} from '../../../promises';
import {useHomePageLayoutActions} from '../../../../page-layout/utils/home-page';
import {arrayMove} from '../../../../../utils/array';
import {useToastActions} from '../../../../toast-alert/utils';

const useStyles = makeStyles(theme => ({
  addBannerButton: {
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: colors.grey500,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '.75rem',
    paddingBlock: '.85rem',
  },
  bannerList: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& > *': {
      borderBottom: `1px solid ${colors.grey300}`,
    },

    '& > *:last-child': {
      borderBottom: 'none',
    },
  },
  loader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1rem',
  },
  disabledRoot: {
    background: colors.grey300,
  },
  bannerItem: {
    gap: '.25rem',
    overflow: 'hidden',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '5% 95%',
    alignItems: 'center',
  },
}));

const MAX_BANNER_BY_GROUP = 5;
// const MAX_BANNER_GROUPS = 5;

type Props = {
  bannerGroup?: BannerGroup,
  sectionEnabled?: boolean,
};

export default function BannerGroupSection(props: Props) {
  const {bannerGroup, sectionEnabled} = props;

  const classes = useStyles();

  const {removeSection} = useHomePageLayoutActions();
  const {showToast} = useToastActions();

  const hasBannerFeature = useIsFeatureInTier('BANNER');
  const {showTierRestrictionModal} = useTierRestrictionActions();
  const {deleteBanner, loading: deletingBanner} = useBannerActions();
  const {
    toggleEnabledBanner,
    reorderBanners,
    loading: reordering,
  } = useBannerGroupActions();

  const [banners, setBanners] = useState<Banner[]>([]);
  const loading = deletingBanner || reordering;

  useEffect(() => {
    if (!bannerGroup || loading) return;
    if (bannerGroup.banners.length > 0) return;

    deleteBannerGroupService(bannerGroup.id).then(response => {
      removeSection('STORE_BANNER', bannerGroup.id);
    });
  }, [bannerGroup, loading, removeSection]);

  useEffect(() => {
    if (!bannerGroup) return;
    setBanners(bannerGroup.banners);
  }, [bannerGroup]);

  if (!bannerGroup || bannerGroup.banners.length === 0) {
    return null;
  }

  const chipLabel = hasBannerFeature ? 'Nuevo' : 'PRO +';
  const addBannerButtonDisabled =
    bannerGroup.banners.length >= MAX_BANNER_BY_GROUP;

  if (bannerGroup.banners.length === 0) return null;

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    const oldOrder = [...banners];
    const newOrder = arrayMove(banners, oldIndex, newIndex);

    setBanners(newOrder);
    reorderBanners(bannerGroup.id, newOrder)
      .then(() => {
        showToast({
          title: 'Orden guardado',
          message: 'El orden de los banners ha sido actualizado',
          type: 'success',
        });
      })
      .catch(() => {
        showToast({
          title: 'Algo salió mal',
          message: 'No se pudo guardar el orden de los banners',
          type: 'danger',
        });
        setBanners(oldOrder);
      });
  };

  return (
    <>
      <SwitchableSection
        id={bannerGroup.id}
        title="Banners"
        chipLabel={chipLabel}
        disabled={!hasBannerFeature}
        classes={{
          root: !sectionEnabled ? classes.disabledRoot : '',
        }}
      >
        <Button
          fullWidth
          id={bannerGroup.id}
          className={classes.addBannerButton}
          component={Link}
          to={`/my-store/pages/home-page/banner/${bannerGroup.id}/new`}
          disabled={addBannerButtonDisabled}
          onClick={e => {
            if (!hasBannerFeature) {
              e.preventDefault();
              showTierRestrictionModal();
            }
          }}
        >
          <Add />
          {addBannerButtonDisabled
            ? 'Máximo número de banners'
            : 'Agregar nuevo banner'}
        </Button>

        <SortableList draggedItemClassName="dragged" onSortEnd={handleSortEnd}>
          <section className={classes.bannerList}>
            {banners.map((banner, index) => {
              return (
                <SortableItem key={banner.id}>
                  <div className={classes.bannerItem}>
                    <SortableKnob>
                      <div className={classes.dragHandle}>
                        <DragIndicator />
                      </div>
                    </SortableKnob>
                    <BannerItem
                      banner={banner}
                      featureDisabled={!hasBannerFeature}
                      onToggleEnabled={() => {
                        toggleEnabledBanner(bannerGroup.id, banner.id);
                      }}
                      onDelete={() => {
                        deleteBanner(bannerGroup.id, banner.id);
                      }}
                    />
                  </div>
                </SortableItem>
              );
            })}

            {loading && (
              <div className={classes.loader}>
                <CircularProgress size={48} />
              </div>
            )}
          </section>
        </SortableList>
      </SwitchableSection>

      <BannerPreview />
    </>
  );
}
